import { FormArray, FormBuilder, FormGroup } from '@angular/forms';

import { VendaParcela } from '../../../../../shared/services/models/vendas/venda-parcela.model';
import { VendaRateioCentroCusto } from '../../../../../shared/services/models/vendas/venda-rateio-centro-custo.model';
import { VendaPagamentoSalvarStruct } from '../../../../../shared/services/structs/vendas/venda-pagamento-salvar.struct';
import { VendaPagamentoStruct } from '../../../../../shared/services/structs/vendas/venda-pagamento.struct';
import { montarFormParcela, montarFormRateio, montarFormRecebimento } from './venda-pagamento.form';

/**
 * A tradução entre o recebimento do SERVIDOR e o do FORMULÁRIO, nas duas direções — o mesmo papel
 * que `venda-item.mapa.ts` faz para o item.
 *
 * Existe pela mesma razão: `patchValue` não cria controles de `FormArray`. Com os arrays de
 * recebimentos e de rateio nascendo vazios, o documento carregado passava por eles em silêncio e
 * o PUT seguinte — que apaga e regrava os filhos — destruía recebimentos, parcelas e rateio de um
 * documento que o usuário só abriu e salvou.
 */
export function montarRecebimentoDoServidor(fb: FormBuilder, recebimento: VendaPagamentoStruct): FormGroup {
    const grupo = montarFormRecebimento(fb);

    grupo.patchValue(
        {
            idVendaPagamento: recebimento.idVendaPagamento,
            sequencia: recebimento.sequencia,
            valor: recebimento.valor,
            idFormaRecebimento: recebimento.idFormaRecebimento,
            idCondicaoPagamento: recebimento.idCondicaoPagamento,
            condicaoPagamentoLivre: recebimento.condicaoPagamentoLivre,
            idCategoria: recebimento.idCategoria,
            idContaFinanceira: recebimento.idContaFinanceira,
            idIntermediadorPagamento: recebimento.idIntermediadorPagamento,
            idIntermediadorBandeira: recebimento.idIntermediadorBandeira,
        },
        { emitEvent: false },
    );

    const parcelas = grupo.get('parcelas') as FormArray;

    for (const parcela of recebimento.parcelas ?? []) {
        const linha = montarFormParcela(fb);

        linha.patchValue(
            {
                idVendaParcela: parcela.idVendaParcela,
                numeroParcela: parcela.numeroParcela,
                dias: parcela.dias,
                dataVencimento: parcela.dataVencimento,
                valor: parcela.valor,
                isValorFixadoUsuario: parcela.isValorFixadoUsuario,
                nsu: parcela.nsu,
                observacao: parcela.observacao,
            },
            { emitEvent: false },
        );

        parcelas.push(linha, { emitEvent: false });
    }

    return grupo;
}

/**
 * O caminho de volta. As parcelas saem no formato do MODEL `VendaParcela`, que é o que o contrato
 * de gravação espera: os campos do servidor (`idEmpresa`, `idVendaPagamento`, `numeroParcela`,
 * `datetimeInclusao`) vão preenchidos com zero ou com o instante atual porque o C# os declara
 * não-anuláveis — mandar nulo derrubaria o request no ModelState — e o servidor os reescreve.
 */
export function achatarRecebimentoParaRequest(grupo: FormGroup): VendaPagamentoSalvarStruct {
    const bruto = grupo.getRawValue();
    const parcelas: VendaParcela[] = ((grupo.get('parcelas') as FormArray).controls as FormGroup[]).map(
        (linha, indice) => {
            const p = linha.getRawValue();

            return {
                idVendaParcela: Number(p.idVendaParcela ?? 0) || 0,
                idEmpresa: 0,
                idVendaPagamento: Number(bruto.idVendaPagamento ?? 0) || 0,
                numeroParcela: indice + 1,
                dias: p.dias ?? null,
                dataVencimento: paraDataDeCalendario(p.dataVencimento) ?? '',
                valor: Number(p.valor ?? 0) || 0,
                isValorFixadoUsuario: p.isValorFixadoUsuario === true,
                nsu: p.nsu ?? null,
                observacao: p.observacao ?? null,
                datetimeInclusao: new Date().toISOString(),
            };
        },
    );

    return {
        valor: bruto.valor ?? null,
        idFormaRecebimento: bruto.idFormaRecebimento ?? null,
        idCondicaoPagamento: bruto.idCondicaoPagamento ?? null,
        condicaoPagamentoLivre: bruto.condicaoPagamentoLivre || null,
        idCategoria: bruto.idCategoria ?? null,
        idContaFinanceira: bruto.idContaFinanceira ?? null,
        idIntermediadorPagamento: bruto.idIntermediadorPagamento ?? null,
        idIntermediadorBandeira: bruto.idIntermediadorBandeira ?? null,
        parcelas,
    };
}

export function montarRateioDoServidor(fb: FormBuilder, rateio: VendaRateioCentroCusto): FormGroup {
    const grupo = montarFormRateio(fb);

    grupo.patchValue(
        {
            idVendaRateioCentroCusto: rateio.idVendaRateioCentroCusto,
            idCentroCusto: rateio.idCentroCusto,
            valor: rateio.valor,
            percentual: rateio.percentual,
        },
        { emitEvent: false },
    );

    return grupo;
}

export function achatarRateioParaRequest(grupo: FormGroup): VendaRateioCentroCusto {
    const bruto = grupo.getRawValue();

    return {
        idVendaRateioCentroCusto: Number(bruto.idVendaRateioCentroCusto ?? 0) || 0,
        idEmpresa: 0,
        idVenda: 0,
        idCentroCusto: Number(bruto.idCentroCusto ?? 0) || 0,
        valor: bruto.valor ?? null,
        percentual: bruto.percentual ?? null,
        datetimeInclusao: new Date().toISOString(),
    };
}

/**
 * Data de CALENDÁRIO para o servidor: o datepicker entrega `Date`, o documento carregado entrega a
 * string sem fuso que veio do GET. Nos dois casos o que interessa é o dia — e a folha aplica
 * `.Date`, então o instante local à meia-noite em ISO chega no mesmo dia.
 */
export function paraDataDeCalendario(valor: unknown): string | null {
    if (valor == null || valor === '') return null;
    if (valor instanceof Date) return isNaN(valor.getTime()) ? null : valor.toISOString();

    return String(valor);
}
